import { useState, useRef, useEffect } from 'react';
import {
  Card,
  Stack,
  Title,
  TextInput,
  Text,
  Box,
  Container,
} from '@mantine/core';
import GameHeader from '../components/GameHeader';
import PlayerDrawingScreen from './5-PlayerDrawingScreen';
import SketchButton from '../components/SketchButton';

function JoinGameScreen({
  gameName,
  roomCode,
  setRoomCode,
  playerName,
  setPlayerName,
  onJoinGame,
  statusMessage,
  connectionStatus,
  retryCount,
  socket,
}) {
  const [stage, setStage] = useState('code'); // 'code', 'name', or 'drawing'
  const [tempRoomCode, setTempRoomCode] = useState(roomCode);
  const [tempPlayerName, setTempPlayerName] = useState(playerName);
  const [validating, setValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const roomCodeRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    if (stage === 'code' && roomCodeRef.current) {
      roomCodeRef.current.focus();
    }
    if (stage === 'name' && nameRef.current) {
      nameRef.current.focus();
    }
  }, [stage]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    const code = tempRoomCode.trim().toUpperCase();
    if (!code) return;
    if (!socket) return;

    setValidating(true);
    setCodeError('');

    socket.emit('validate-room', { roomCode: code }, (response) => {
      setValidating(false);
      if (response.valid) {
        setRoomCode(code);
        setStage('name');
      } else {
        setCodeError('Room not found');
      }
    });
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!tempPlayerName.trim()) return;
    setPlayerName(tempPlayerName);
    setStage('drawing');
  };

  const handleDrawingSubmit = (drawingData) => {
    const fakeEvent = { preventDefault: () => {} };
    onJoinGame(fakeEvent, drawingData);
  };

  // Stage 3: Draw yourself
  if (stage === 'drawing') {
    return (
      <PlayerDrawingScreen
        gameName={gameName}
        onSubmitDrawing={handleDrawingSubmit}
        disabled={false}
        buttonLabel="JOIN GAME"
        instruction={{ text: 'Draw yourself!', color: 'purple' }}
        roomCode={roomCode}
        onQuit={() => setStage('name')}
      />
    );
  }

  // Stage 2: Enter name
  if (stage === 'name') {
    return (
      <Box style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <GameHeader
          gameName={gameName}
          roomCode={roomCode}
          onQuit={() => setStage('code')}
        />
        <Box
          style={{
            flex: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Container size="sm" p="md" w="100%">
            <Card shadow="xl" p="xl" radius="lg" withBorder>
              <Stack gap="xl">
                <Box ta="center">
                  <Title order={1} size="2rem" c="purple">
                    What's your name?
                  </Title>
                </Box>

                <form onSubmit={handleNameSubmit}>
                  <Stack gap="lg">
                    <TextInput
                      ref={nameRef}
                      placeholder="YOUR NAME"
                      value={tempPlayerName}
                      onChange={(e) => setTempPlayerName(e.target.value)}
                      size="xl"
                      maxLength={20}
                      required
                      styles={{
                        input: {
                          fontSize: '1.4rem',
                          textAlign: 'center',
                          letterSpacing: '2px',
                          fontWeight: 600,
                          fontFamily: '"Pangolin", "Fredericka the Great", "Rock Salt", cursive',
                          backgroundColor: 'rgba(255,255,255,0.6)',
                          border: '3px solid #4a8c5c',
                          borderRadius: 12,
                          color: '#333',
                          height: '3.5rem',
                        },
                      }}
                    />

                    <SketchButton
                      type="submit"
                      size="lg"
                      fullWidth
                      disabled={!tempPlayerName.trim()}
                    >
                      Next
                    </SketchButton>
                  </Stack>
                </form>
              </Stack>
            </Card>
          </Container>
        </Box>
      </Box>
    );
  }

  // Stage 1: Enter room code
  return (
    <Box
      style={{
        backgroundImage: 'url(/join-top-bg.png), url(/join-bottom-bg.png)',
        backgroundPosition: 'top center, bottom center',
        backgroundSize: '100% auto, 100% auto',
        backgroundRepeat: 'no-repeat, no-repeat',
        height: '100dvh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
      }}
    >
      <form onSubmit={handleCodeSubmit} style={{ width: '100%', maxWidth: 'min(300px, 80vw)' }}>
        <Stack gap="md" align="center" style={{ marginTop: '3vh' }}>
          <TextInput
            ref={roomCodeRef}
            value={tempRoomCode}
            onChange={(e) => { setTempRoomCode(e.target.value); setCodeError(''); }}
            size="lg"
            maxLength={6}
            required
            styles={{
              input: {
                textTransform: 'uppercase',
                fontSize: '3rem',
                textAlign: 'center',
                letterSpacing: '4px',
                fontWeight: 700,
                border: 'none',
                borderRadius: 8,
                color: '#3B82F6',
                fontFamily: '"Pangolin", "Fredericka the Great", "Rock Salt", cursive',
                height: '5rem',
                  WebkitTextStroke: '0.5px rgba(37, 99, 235, 0.3)',

              },
              wrapper: {
                width: '100%',
              },
            }}
          />

          <Box style={{ marginTop: 40 }}>
            <SketchButton
              type="submit"
              disabled={!tempRoomCode.trim() || connectionStatus !== 'connected' || validating}
            >
              {validating ? 'CHECKING...' : 'PLAY'}
            </SketchButton>
          </Box>

          {codeError && (
            <Text size="sm" c="red" ta="center" fw={600}
              style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '4px 12px' }}
            >
              {codeError}
            </Text>
          )}

          {connectionStatus !== 'connected' && (
            <Text size="sm" c="red" ta="center" fw={600}
              style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '4px 12px' }}
            >
              {connectionStatus === 'connecting' && `Connecting${retryCount > 0 ? ` (attempt ${retryCount + 1})` : ''}...`}
              {connectionStatus === 'disconnected' && 'Reconnecting...'}
              {connectionStatus === 'error' && 'Connection error - Retrying...'}
              {connectionStatus === 'failed' && 'Connection failed - Please refresh'}
            </Text>
          )}

          {statusMessage && (
            <Text size="sm" ta="center" fw={600}
              style={{
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: 8,
                padding: '4px 12px',
                color: statusMessage.includes('Failed') ? '#dc3545' : '#4CAF50',
              }}
            >
              {statusMessage}
            </Text>
          )}
        </Stack>
      </form>
    </Box>
  );
}

export default JoinGameScreen;
